import React, { FC, useState, useEffect } from 'react';
import { useStore } from 'hooks/useRootStore';
import { useAppPreloader } from 'hooks/useAppPreloader';
import { useMutation, useQuery } from 'react-query';
import { useHistory } from 'react-router';
import { useForm, Controller } from 'react-hook-form';
import Reaptcha from 'reaptcha';
import {
  Paper,
  Grid,
  Container,
  Button,
  TextField,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  IconButton,
  FormHelperText,
} from '@material-ui/core';
import MuiPhoneNumber from 'material-ui-phone-number-2';
import { useStyles } from './styles';
import { UserFormInputPattern, EndUserData, GenericFormProps } from './types';
import { observer } from 'mobx-react-lite';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import { getUrlSearchValue } from 'utils/urlUtils';
export const EndUserNewPasswordForm: FC<GenericFormProps> = observer(() => {
  const classes = useStyles();
  const history = useHistory();
  const [isVerified, setIsVerified] = useState(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [passLowerCase, setPassLowerCase] = useState<number>(0);
  const [passUpperCase, setPassUpperCase] = useState<number>(0);
  const [passNumericCase, setPassNumericCase] = useState<number>(0);
  const [passSpecialCase, setPassSpecialCase] = useState<number>(0);
  const [minPass, SetMinPass] = useState<number>(0);
  const {
    ldapStore: { clickedUser, createNewPassword, validateToken },
    endUserSettingsFormStore: { getSetting, settingsByTypeView },
    notificationStore: { openNotification },
    appPreloaderStore: { disablePreloader },
  } = useStore();

  const passwordRegex = new RegExp(
    `^(?=(?:.*[A-Z]){${passUpperCase},})(?=(?:.*[a-z]){${passLowerCase},})(?=(?:.*\\d){${passNumericCase},})(?=(?:.*[!@#$%^&*()_+={}[]|\\:;"'<>,.?/~\`-]){${passSpecialCase},}).{${minPass},}$`
  );
  const isStrongPassword = (password: any) => {
    return passwordRegex.test(password);
  };
  const [emailValue] = useState<string>(getUrlSearchValue('email'));
  const [userpassword, setUserpassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const tokenValidateReq = useMutation((data: any) => validateToken(data), {
    mutationKey: 'tokenValidateReq',
    onSuccess: () => {
      disablePreloader();
    },
    onError: ({ message }: Error) => {
      openNotification('error', message);
      disablePreloader();
    },
  });

  const getSettingsReq = useQuery('getSettingReq', () => getSetting(), {
    onError: () => openNotification('error', `Can't get password policy`),
  });

  useEffect(() => {
    settingsByTypeView('password').forEach((group: any, i: any) =>
      group.settings.forEach((setting: any, j: any) => {
        if (setting.key === 'minimumNumberOfLowercaseCharacters') {
          return setPassLowerCase(+setting.value);
        } else if (setting.key === 'minimumNumberOfUppercaseCharacters') {
          return setPassUpperCase(+setting.value);
        } else if (setting.key === 'minimumNumberOfNumericCharacters') {
          return setPassNumericCase(+setting.value);
        } else if (setting.key === 'minimumNumberOfSpecialCharacters') {
          return setPassSpecialCase(+setting.value);
        } else if (setting.key === 'minimumLengthPassword') {
          return SetMinPass(+setting.value);
        } else {
          return 0;
        }
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getSettingsReq.isFetching]);

  useEffect(() => {
    const modifiedData = {
      token: getUrlSearchValue('reset-token'),
    };
    return tokenValidateReq.mutate(modifiedData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const createNewPasswordReq = useMutation((data: EndUserData) => createNewPassword(data), {
    mutationKey: 'createNewPasswordReq',
    onSuccess: () => {
      openNotification('success', 'Created Successfully');
      history.push(handleCompanyUrl('/login'));
      disablePreloader();
    },
    onError: ({ message }: Error) => {
      openNotification('error', message);
      disablePreloader();
    },
  });

  const { control, handleSubmit } = useForm<EndUserData>();
  const onSubmit = (data: EndUserData) => {
    const modifiedData = {
      ...data,
      mail: getUrlSearchValue('email'),
      token: getUrlSearchValue('reset-token'),
    };
    return createNewPasswordReq.mutate(modifiedData);
  };
  const mockDataPers1: UserFormInputPattern[] = [
    {
      label: 'UserID (Optional)',
      key: 'uid',
      required: false,
      pattern: {
        regexp: /^[0-9a-z_]+$/,
        errorMessage: 'This field must contain only digits, lowercase letters or underscores',
      },
    },
    { label: 'Email', key: 'email' },
    { label: 'Password', type: 'password', key: 'password', required: !clickedUser as boolean },
    { label: 'Re-type Password', type: 'password', key: 'confirmPassword', required: !clickedUser as boolean },
  ];
  useAppPreloader(createNewPasswordReq.isLoading || tokenValidateReq.isLoading);
  return tokenValidateReq.isSuccess ? (
    <form onSubmit={handleSubmit(onSubmit)} className={classes.box}>
      <Grid container justify='center'>
        <Paper className={classes.paper}>
          <p className={classes.boxTitle}>New Password</p>
          <Grid container>
            <Container className={classes.tableBox}>
              {mockDataPers1.map((item, index) => (
                <Controller
                  name={item.key}
                  control={control}
                  rules={{ required: item?.required }}
                  render={({ field, fieldState: { invalid, error } }) => (
                    <Grid key={index} container className={item?.multiline ? classes.rowWide : classes.row}>
                      {item.type === 'password' ? (
                        <FormControl className={invalid ? classes.inputError : classes.input} required>
                          <InputLabel variant='outlined' htmlFor={item?.key}>
                            {item.label}
                          </InputLabel>
                          <OutlinedInput
                            {...field}
                            error={invalid || userpassword !== confirmPassword}
                            id={item?.key}
                            label={item?.label}
                            fullWidth
                            value={field.value}
                            onChange={(e) => {
                              field.onChange(e);
                              item?.key === 'password'
                                ? setUserpassword(e.target.value)
                                : item?.key === 'confirmPassword'
                                ? setConfirmPassword(e.target.value)
                                : console.log('failed to get password');
                            }}
                            required={item?.required}
                            type={showPassword ? 'text' : 'password'}
                            endAdornment={
                              <InputAdornment position='end'>
                                <IconButton
                                  aria-label='toggle password visibility'
                                  onClick={() => setShowPassword(!showPassword)}
                                >
                                  {showPassword ? <Visibility /> : <VisibilityOff />}
                                </IconButton>
                              </InputAdornment>
                            }
                          />
                          {userpassword !== confirmPassword ? (
                            <FormHelperText error id={item?.key}>
                              Confirm Password does not match
                            </FormHelperText>
                          ) : !isStrongPassword(userpassword) ? (
                            <FormHelperText error id={item?.key}>
                              Password must contain at least ${passUpperCase} uppercase, ${passLowerCase} lowercase, $
                              {passSpecialCase} special characters, ${passNumericCase} numeric value(s), and minimum $
                              {minPass} characters.
                            </FormHelperText>
                          ) : null}
                        </FormControl>
                      ) : item.type === 'phone' ? (
                        <FormControl className={invalid ? classes.inputError : classes.borderClass}>
                          <MuiPhoneNumber
                            className={invalid ? classes.inputError : classes.borderClass}
                            {...field}
                            error={invalid}
                            variant='outlined'
                            fullWidth
                            name={item?.key}
                            label={item?.label}
                            required={item?.required}
                            data-cy='user-phone'
                            defaultCountry={'us'}
                            classes='input-danger'
                          />
                        </FormControl>
                      ) : item.key === 'email' ? (
                        <TextField
                          {...field}
                          autoFocus={item?.autoFocus}
                          variant='outlined'
                          value={emailValue}
                          disabled
                          className={invalid ? classes.inputError : classes.input}
                          multiline={item?.multiline}
                          label={item?.label}
                          required={item?.required}
                          rows={item?.multiline ? 4 : 1}
                          placeholder={item.placeholder}
                          type={item.type ? item.type : undefined}
                          error={invalid}
                          helperText={
                            invalid ? (error?.type === 'pattern' ? item.pattern?.errorMessage : 'Required field') : ''
                          }
                        />
                      ) : (
                        <TextField
                          {...field}
                          autoFocus={item?.autoFocus}
                          variant='outlined'
                          className={invalid ? classes.inputError : classes.input}
                          multiline={item?.multiline}
                          label={item?.label}
                          required={item?.required}
                          rows={item?.multiline ? 4 : 1}
                          placeholder={item.placeholder}
                          type={item.type ? item.type : undefined}
                          error={invalid}
                          helperText={
                            invalid ? (error?.type === 'pattern' ? item.pattern?.errorMessage : 'Required field') : ''
                          }
                        />
                      )}
                    </Grid>
                  )}
                />
              ))}
              <div className={classes.captcha}>
                <Reaptcha
                  className={classes.recaptcha}
                  sitekey='6LdjPPIgAAAAAOceFAN1uID-_6RAbllD2E4bfWRb'
                  onVerify={() => {
                    setIsVerified(true);
                  }}
                  onExpire={() => {
                    setIsVerified(false);
                  }}
                />
              </div>
            </Container>
            <Button disabled={!isVerified} className={classes.button} variant='contained' color='primary' type='submit'>
              Submit
            </Button>
          </Grid>
        </Paper>
      </Grid>
    </form>
  ) : tokenValidateReq.isError ? (
    <div className={classes.mainError}>
      <div>
        <h1 className={classes.title}>YOU HAVE ALREADY UPDATED YOUR PASSWORD USING THIS LINK.</h1>
      </div>
      <div>
        {/* <Button
          type='submit'
          variant='contained'
          color='primary'
          onClick={() => history.push(handleOrganizationUrl(UrlExtracted, '/user/forgot-password'))}
        >
          Forget Password
        </Button> */}
      </div>
    </div>
  ) : (
    <p></p>
  );
});
function handleCompanyUrl(arg0: string): import('history').LocationDescriptor<unknown> {
  throw new Error('Function not implemented.');
}
